import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { endpoints, baseUrl } from '../../../api/api';

interface FunnelData {
  signups: number;
  profileComplete: number;
  bookCreated: number;
  bookSubmitted: number;
  bookApproved: number;
}

const TIMEFRAMES = [
  { label: 'All time', days: null },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 7d',  days: 7 },
];

function pct(num: number, den: number): string {
  if (!den) return '—';
  return `${Math.round((num / den) * 100)}%`;
}

const Arrow: React.FC<{ from: number; to: number }> = ({ from, to }) => {
  const rate = from ? Math.round((to / from) * 100) : 0;
  const color = rate >= 50 ? 'text-green-600' : rate >= 25 ? 'text-amber-500' : 'text-red-400';
  return (
    <div className="flex flex-col items-center justify-center px-1 shrink-0">
      <span className={`text-xs font-semibold ${color}`}>{rate}%</span>
      <svg className="w-5 h-5 text-gray-300 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
};

interface StepProps {
  label: string;
  value: number;
  total: number;
  accent?: boolean;
}

const Step: React.FC<StepProps> = ({ label, value, total, accent }) => (
  <div className={`flex flex-col items-center bg-white rounded-lg border px-4 py-3 min-w-[100px] ${accent ? 'border-orange-300 shadow-sm' : 'border-gray-200'}`}>
    <span className={`text-2xl font-bold ${accent ? 'text-orange-600' : 'text-gray-900'}`}>{value.toLocaleString()}</span>
    <span className="text-xs text-gray-500 text-center leading-tight mt-1">{label}</span>
    {total !== value && (
      <span className="text-xs text-gray-400 mt-1">{pct(value, total)} of total</span>
    )}
  </div>
);

const ConversionFunnel: React.FC = () => {
  const [funnel, setFunnel]       = useState<FunnelData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [activeDays, setActiveDays] = useState<number | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(false);
      try {
        const token  = localStorage.getItem('accessToken');
        const params = activeDays ? `?days=${activeDays}` : '';
        const res    = await axios.get(`${baseUrl}${endpoints.adminGetFunnel}${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFunnel(res.data.funnel);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [activeDays]);

  return (
    <div className="px-5 py-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-400">Unique users at each stage</p>
        <div className="flex gap-1">
          {TIMEFRAMES.map(tf => (
            <button
              key={String(tf.days)}
              onClick={() => setActiveDays(tf.days)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                activeDays === tf.days
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-500 hover:bg-gray-200'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
        </div>
      )}

      {error && !loading && (
        <p className="text-xs text-red-400 text-center py-4">Failed to load funnel data</p>
      )}

      {funnel && !loading && (
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          <Step label="Signed Up"        value={funnel.signups}        total={funnel.signups} accent />
          <Arrow from={funnel.signups}        to={funnel.profileComplete} />
          <Step label="Profile Complete" value={funnel.profileComplete} total={funnel.signups} />
          <Arrow from={funnel.profileComplete} to={funnel.bookCreated} />
          <Step label="Book Created"     value={funnel.bookCreated}    total={funnel.signups} />
          <Arrow from={funnel.bookCreated}    to={funnel.bookSubmitted} />
          <Step label="Submitted"        value={funnel.bookSubmitted}  total={funnel.signups} />
          <Arrow from={funnel.bookSubmitted}  to={funnel.bookApproved} />
          <Step label="Approved"         value={funnel.bookApproved}   total={funnel.signups} />
        </div>
      )}
    </div>
  );
};

export default ConversionFunnel;
